import { useState, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router'
import type { SortingState } from '@/admin/components/data-table'

interface UseTableStateOptions {
  defaultPageSize?: number
  defaultSorting?: SortingState | null
}

interface TableState {
  page: number
  pageSize: number
  sorting: SortingState | null
  setPage: (page: number) => void
  setPageSize: (size: number) => void
  setSorting: (sorting: SortingState | null) => void
  /** Ready-to-use query params for React Query hooks (page, page_size, ordering) */
  queryParams: Record<string, string>
}

/**
 * Manages table pagination + sorting state, synchronised with URL search params.
 * Replaces `usePagination` per-page and adds sorting support.
 *
 * Usage:
 *   const { page, pageSize, sorting, setPage, setPageSize, setSorting, queryParams } = useTableState('registrations')
 *   const { data } = useHoldings(queryParams)
 */
export function useTableState(
  _tableId: string,
  options: UseTableStateOptions = {},
): TableState {
  const { defaultPageSize = 20, defaultSorting = null } = options
  const [searchParams, setSearchParams] = useSearchParams()

  // Read initial values from URL
  const urlPage = searchParams.get('page')
  const urlPageSize = searchParams.get('page_size')
  const urlOrdering = searchParams.get('ordering')

  // Local state (source of truth - syncs to URL)
  const [page, setPageRaw] = useState(() => {
    const n = urlPage ? parseInt(urlPage, 10) : 1
    return isNaN(n) || n < 1 ? 1 : n
  })

  const [pageSize, setPageSizeRaw] = useState(() => {
    const n = urlPageSize ? parseInt(urlPageSize, 10) : defaultPageSize
    return isNaN(n) || n < 1 ? defaultPageSize : n
  })

  const [sorting, setSortingRaw] = useState<SortingState | null>(() => {
    if (urlOrdering) {
      const desc = urlOrdering.startsWith('-')
      const field = desc ? urlOrdering.slice(1) : urlOrdering
      return { field, direction: desc ? 'desc' : 'asc' }
    }
    return defaultSorting
  })

  // Sync to URL params
  const updateUrl = useCallback(
    (p: number, ps: number, s: SortingState | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set('page', String(p))
          next.set('page_size', String(ps))
          if (s) {
            next.set('ordering', s.direction === 'desc' ? `-${s.field}` : s.field)
          } else {
            next.delete('ordering')
          }
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const setPage = useCallback(
    (p: number) => {
      setPageRaw(p)
      updateUrl(p, pageSize, sorting)
    },
    [pageSize, sorting, updateUrl],
  )

  const setPageSize = useCallback(
    (ps: number) => {
      setPageSizeRaw(ps)
      setPageRaw(1)
      updateUrl(1, ps, sorting)
    },
    [sorting, updateUrl],
  )

  const setSorting = useCallback(
    (s: SortingState | null) => {
      setSortingRaw(s)
      setPageRaw(1) // Reset to page 1 on sort change
      updateUrl(1, pageSize, s)
    },
    [pageSize, updateUrl],
  )

  const queryParams = useMemo(() => {
    const params: Record<string, string> = {
      page: String(page),
      page_size: String(pageSize),
    }
    if (sorting) {
      params.ordering =
        sorting.direction === 'desc' ? `-${sorting.field}` : sorting.field
    }
    return params
  }, [page, pageSize, sorting])

  return {
    page,
    pageSize,
    sorting,
    setPage,
    setPageSize,
    setSorting,
    queryParams,
  }
}
